import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  type StyleProp,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';
import { BodyText, Container, theme } from '@sg/garnish';

import { IngredientModificationsTypes } from '@order/utils';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationSelectedIngredientKindSection = (
  props: CustomizationSelectedIngredientKindSectionProps,
) => {
  const { ingredientKindName, formattedQuantity, style, children } = props;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Container style={[styles.container, style]}>
      <View style={styles.label}>
        <View>
          <BodyText sizeMatch={['16']}>
            <Text style={styles.ingredientKindName}>{ingredientKindName}</Text>
          </BodyText>

          {ingredientKindName === IngredientModificationsTypes.Dressings ? (
            <BodyText style={styles.helperText} sizeMatch={['16']}>
              <FormattedMessage {...messages.helperText} />
            </BodyText>
          ) : null}
        </View>

        <BodyText sizeMatch={['16']}>{formattedQuantity}</BodyText>
      </View>

      {children}
    </Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  helperText: {
    defaultMessage: 'Portions + details on next page',
    description:
      'Customization | Selected Ingredients | Dressings | Helper text',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    padding: theme.spacing['4'],
    borderBottomWidth: 1,
    borderColor: theme.colors.GRAY,
    rowGap: theme.spacing['2'],
  },
  label: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing['2'],
  },
  ingredientKindName: {
    fontWeight: 700,
    textTransform: 'capitalize',
  },
  helperText: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationSelectedIngredientKindSectionProps = {
  ingredientKindName: string;
  formattedQuantity: string;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
};
